import api from '../services/api';

/**
 * Get all helpdesk tickets with pagination and filtering
 * Uses automatic masquerading via API service
 */
export const getTickets = async (filters = {}) => {
  try {
    // API interceptor automatically handles masquerading headers
    // Request a large limit to get all tickets for client-side filtering/pagination
    const params = { 
      ...filters,
      limit: 10000 // High limit to get all tickets
    };
    const response = await api.get('/api/helpdesk/tickets', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching tickets:', error);
    throw new Error('Failed to fetch tickets: ' + error.message);
  }
};

/**
 * Get helpdesk rating records (IT-only)
 */
export const getHelpdeskRatings = async (filters = {}) => {
  try {
    const response = await api.get('/api/helpdesk/ratings', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching helpdesk ratings:', error);
    throw error;
  }
};

/**
 * Clean up uncompleted ratings (IT-only)
 */
export const cleanupUncompletedRatings = async (daysOld = 30) => {
  try {
    const response = await api.delete('/api/helpdesk/ratings/cleanup', {
      params: { days_old: daysOld }
    });
    return response.data;
  } catch (error) {
    console.error('Error cleaning up uncompleted ratings:', error);
    throw error;
  }
};

/**
 * Mark all ratings as seen (IT-only)
 */
export const markRatingsAsSeen = async () => {
  try {
    const response = await api.post('/api/notifications/mark-ratings-seen');
    return response.data;
  } catch (error) {
    console.error('Error marking ratings as seen:', error);
    throw error;
  }
};

/**
 * Get ticket details by ID
 */
export const getTicketDetails = async (ticketId) => {
  try {
    const response = await api.get(`/api/helpdesk/tickets/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ticket details:', error);
    return { status: 0, message: error.message, ticket: null };
  }
};

/**
 * Create a new ticket
 */
export const createTicket = async (ticketData) => {
  try {
    // If we have attachments, use FormData; otherwise use JSON
    let requestData = ticketData;
    let config = {};
    
    if (ticketData.attachments && ticketData.attachments.length > 0) {
      const formData = new FormData();
      
      // Add text fields
      formData.append('title', ticketData.title);
      formData.append('description', ticketData.description);
      formData.append('priority', ticketData.priority);
      formData.append('category', ticketData.category);
      formData.append('system_id', ticketData.system_id || '');
      formData.append('type', ticketData.type || 'support');
      
      // Add custom_fields for workflow tickets (new_user, staff_movement, access_modification, etc.)
      if (ticketData.custom_fields) {
        formData.append('custom_fields', JSON.stringify(ticketData.custom_fields));
      }
      
      // Add files
      ticketData.attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
      
      requestData = formData;
      config.headers = {
        'Content-Type': 'multipart/form-data'
      };
    }
    
    const response = await api.post('/api/helpdesk/create', requestData, config);
    return response.data;
  } catch (error) {
    console.error('Error creating ticket:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Update ticket
 */
export const updateTicket = async (ticketId, updateData) => {
  try {
    const response = await api.put(`/api/helpdesk/tickets/${ticketId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating ticket:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Assign ticket to user
 */
export const assignTicket = async (ticketId, assignedTo, notes = '') => {
  try {
    const data = {
      assigned_to: assignedTo,
      notes: notes
    };
    
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/assign`, data);
    return response.data;
  } catch (error) {
    console.error('Error assigning ticket:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Resolve ticket
 */
export const resolveTicket = async (ticketId, resolution = '') => {
  try {
    const data = {
      resolution: resolution
    };
    
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/resolve`, data);
    return response.data;
  } catch (error) {
    console.error('Error resolving ticket:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Close ticket
 */
export const closeTicket = async (ticketId, notes = '') => {
  try {
    const data = {
      notes: notes
    };
    
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/close`, data);
    return response.data;
  } catch (error) {
    console.error('Error closing ticket:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Escalate ticket
 */
export const escalateTicket = async (ticketId, escalationType, reason = '') => {
  try {
    const data = {
      escalation_type: escalationType,
      reason: reason
    };
    
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/escalate`, data);
    return response.data;
  } catch (error) {
    console.error('Error escalating ticket:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Handle pause/resume operations with timing tracking
 */
export const pauseResumeTicket = async (ticketId, action) => {
  try {
    const data = {
      action: action // 'start', 'pause', or 'resume'
    };
    
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/pause-resume`, data);
    return response.data;
  } catch (error) {
    console.error('Error processing pause/resume operation:', error);
    return { status: 0, message: error.message };
  }
};



/**
 * Get ticket comments
 */
export const getTicketComments = async (ticketId) => {
  try {
    const response = await api.get(`/api/helpdesk/tickets/${ticketId}/comments`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ticket comments:', error);
    return { status: 0, message: error.message, comments: [] };
  }
};

/**
 * Add comment to ticket
 */
export const addTicketComment = async (ticketId, commentData) => {
  try {
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/comments`, commentData);
    return response.data;
  } catch (error) {
    console.error('Error adding ticket comment:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Get ticket attachments
 */
export const getTicketAttachments = async (ticketId) => {
  try {
    const response = await api.get(`/api/attachments/helpdesk/${ticketId}/list`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ticket attachments:', error);
    return { status: 0, message: error.message, attachments: [] };
  }
};

/**
 * Upload attachment to ticket
 */
export const uploadTicketAttachment = async (ticketId, formData) => {
  try {
    const response = await api.post(`/api/attachments/helpdesk/${ticketId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading ticket attachment:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Get ticket statistics
 */
export const getTicketStats = async () => {
  try {
    const response = await api.get('/api/helpdesk/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching ticket stats:', error);
    return { status: 0, message: error.message, stats: {} };
  }
};

/**
 * Ticket Links (associations)
 */
export const getRelatedTickets = async (ticketId) => {
  try {
    const response = await api.get(`/api/helpdesk/tickets/${ticketId}/related`);
    return response.data;
  } catch (error) {
    console.error('Error fetching related tickets:', error);
    return { status: 0, message: error.message, related: [] };
  }
};

export const createTicketLink = async (ticketId, relatedTicketNumber, relationType = 'related') => {
  try {
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/links`, {
      related_ticket_number: relatedTicketNumber,
      relation_type: relationType
    });
    return response.data;
  } catch (error) {
    console.error('Error creating ticket link:', error);
    return { status: 0, message: error.message };
  }
};

export const deleteTicketLink = async (linkId) => {
  try {
    const response = await api.delete(`/api/helpdesk/ticket-links/${linkId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting ticket link:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Get dashboard data
 */
export const getDashboardData = async () => {
  try {
    const response = await api.get('/api/helpdesk/dashboard');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    return { status: 0, message: error.message, data: {} };
  }
};

/**
 * Search tickets
 */
export const searchTickets = async (query, filters = {}) => {
  try {
    const params = {
      q: query,
      ...filters
    };
    
    const response = await api.get('/api/helpdesk/search', { params });
    return response.data;
  } catch (error) {
    console.error('Error searching tickets:', error);
    return { status: 0, message: error.message, tickets: [] };
  }
};

/**
 * Get IT users for assignment
 */
export const getITUsers = async () => {
  try {
    const response = await api.get('/api/helpdesk/agents');
    return response.data;
  } catch (error) {
    console.error('Error fetching IT users:', error);
    return { status: 0, message: error.message, agents: [] };
  }
};

/**
 * Get pending approvals
 */
export const getPendingApprovals = async () => {
  try {
    const response = await api.get('/api/helpdesk/workflows/pending');
    return response.data;
  } catch (error) {
    console.error('Error fetching pending approvals:', error);
    return { status: 0, message: error.message, approvals: [] };
  }
};

/**
 * Process approval
 */
export const processApproval = async (approvalId, action, notes = '') => {
  try {
    const data = {
      action: action,
      notes: notes
    };
    
    const response = await api.post(`/api/helpdesk/approvals/${approvalId}/process`, data);
    return response.data;
  } catch (error) {
    console.error('Error processing approval:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Get escalated tickets
 */
export const getEscalatedTickets = async () => {
  try {
    const response = await api.get('/api/helpdesk/tickets', { 
      params: { 
        status: 'escalated',
        metadata_only: '1' 
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching escalated tickets:', error);
    return { status: 0, message: error.message, tickets: [] };
  }
};

/**
 * Get approval statistics
 */
export const getTicketApprovalStats = async () => {
  try {
    const response = await api.get('/api/helpdesk/approvals/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching approval stats:', error);
    return { status: 0, message: error.message, stats: {} };
  }
};

/**
 * Get workflow for ticket type
 */
export const getWorkflow = async (ticketType) => {
  try {
    const response = await api.get(`/api/helpdesk/workflow/${ticketType}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching workflow:', error);
    return { status: 0, message: error.message, workflow: null };
  }
};

/**
 * Get SLA information for ticket
 */
export const getSLAInfo = async (ticketId) => {
  try {
    const response = await api.get(`/api/helpdesk/tickets/${ticketId}/sla`);
    return response.data;
  } catch (error) {
    console.error('Error fetching SLA info:', error);
    return { status: 0, message: error.message, sla: null };
  }
};

/**
 * Export tickets data
 */
export const exportTickets = async (filters = {}, format = 'csv') => {
  try {
    const params = {
      format: format,
      ...filters
    };
    
    const response = await api.get('/api/helpdesk/export', { 
      params,
      responseType: 'blob' 
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting tickets:', error);
    throw error;
  }
};

/**
 * Get ticket categories
 */
export const getCategories = async () => {
  try {
    const response = await api.get('/api/helpdesk/categories');
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return { status: 0, message: error.message, categories: [] };
  }
};

/**
 * Process incoming emails to create/update tickets
 * Handles mixed text/JSON responses from Graph API debug output
 */
export const processEmailImport = async () => {
  try {
    const response = await api.post('/api/helpdesk/email/process');
    
    // Handle cases where response might contain debug text before JSON
    let data = response.data;
    
    // If data is a string (mixed output), try to extract JSON
    if (typeof data === 'string') {
      try {
        // Find the last JSON object in the response
        const jsonStart = data.lastIndexOf('{');
        if (jsonStart !== -1) {
          const jsonString = data.substring(jsonStart);
          data = JSON.parse(jsonString);
        }
      } catch (parseError) {
        console.warn('Could not parse mixed response, using as-is:', parseError);
      }
    }
    
    return data;
  } catch (error) {
    console.error('Error processing email import:', error);
    throw new Error('Failed to process email import: ' + error.message);
  }
};

/**
 * Get systems from assets endpoint (systems are managed by assets API)
 */
export const getSystems = async () => {
  try {
    // Request a large page size to get all systems for the dropdown filter
    // The backend defaults to page=1, pageSize=20, which limits results
    // Use public endpoint to avoid permission issues for non-admin users
    const response = await api.get('/api/assets/public_systems', {
      params: {
        page: 1,
        pageSize: 1000  // Request up to 1000 systems (should cover most organizations)
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching systems:', error);
    return { status: 0, message: error.message, systems: [] };
  }
};

/**
 * Escalate ticket to external support
 */
export const escalateTicketToExternal = async (ticketId, escalationData) => {
  try {
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/escalate`, escalationData);
    return response.data;
  } catch (error) {
    console.error('Error escalating ticket to external support:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Get ticket escalation history
 */
export const getTicketEscalationHistory = async (ticketId) => {
  try {
    const response = await api.get(`/api/helpdesk/tickets/${ticketId}/escalations`);
    const data = response.data || {};
    const payload = data.data || data;
    const escalations = payload.escalations || [];
    const status = typeof data.status !== 'undefined' ? data.status : 1;
    return { status, escalations };
  } catch (error) {
    console.error('Error fetching ticket escalation history:', error);
    return { status: 0, message: error.message, escalations: [] };
  }
};

/**
 * Get suppliers for escalation
 */
export const getSuppliers = async () => {
  try {
    const response = await api.get('/api/suppliers?limit=1000');
    return response.data;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    return { status: 0, message: error.message, data: [] };
  }
};

/**
 * Escalate ticket to supplier
 */
export const escalateTicketToSupplier = async (ticketId, escalationData) => {
  try {
    const response = await api.post(`/api/helpdesk/tickets/${ticketId}/escalate/supplier`, escalationData);
    return response.data;
  } catch (error) {
    console.error('Error escalating ticket to supplier:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Get users for the current user's department (for workflow user selection)
 */
export const getDepartmentUsers = async () => {
  try {
    const response = await api.get('/api/helpdesk/department_users');
    return response.data;
  } catch (error) {
    console.error('Error fetching department users:', error);
    return { status: 0, data: { users: [] } };
  }
};

/**
 * Get all users in the system (for access termination - HR function)
 */
export const getAllUsers = async () => {
  try {
    const response = await api.get('/api/helpdesk/all_users');
    return response.data;
  } catch (error) {
    console.error('Error fetching all users:', error);
    return { status: 0, data: { users: [] } };
  }
};

/**
 * Get all departments for workflow ticket forms
 */
export const getHelpdeskDepartments = async () => {
  try {
    const response = await api.get('/api/helpdesk/departments');
    return response.data;
  } catch (error) {
    console.error('Error fetching departments:', error);
    return { status: 0, data: { departments: [] } };
  }
};

/**
 * Get all roles for workflow ticket forms
 */
export const getHelpdeskRoles = async () => {
  try {
    const response = await api.get('/api/helpdesk/roles');
    return response.data;
  } catch (error) {
    console.error('Error fetching roles:', error);
    return { status: 0, data: { roles: [] } };
  }
};

/**
 * Get division departments for New User form
 */
export const getDivisionDepartments = async () => {
  try {
    const response = await api.get('/api/helpdesk/division_departments');
    return response.data;
  } catch (error) {
    console.error('Error fetching division departments:', error);
    return { status: 0, data: { division_departments: [] } };
  }
};

/**
 * Get workflow status for a ticket
 */
export const getTicketWorkflowStatus = async (ticketId) => {
  try {
    const response = await api.get(`/api/helpdesk/tickets/${ticketId}/workflow-status`);
    return response.data;
  } catch (error) {
    console.error('Error fetching workflow status:', error);
    return { status: 0, message: error.message, workflow: null };
  }
};

/**
 * Export a ticket to PDF (Access Management Report format)
 * Opens the PDF in a new browser tab
 */
export const exportTicketPdf = (ticketId) => {
  // Get the base URL from api config
  const baseUrl = api.defaults.baseURL || '';
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  // Open PDF in new tab with auth token
  const url = `${baseUrl}/api/helpdesk/tickets/${ticketId}/export-pdf`;
  
  // Create a form to POST the request with auth header (or use window.open for GET)
  window.open(url, '_blank');
};

/**
 * Start approval workflow for a ticket
 */
export const startApprovalWorkflow = async (ticketId) => {
  try {
    const response = await api.post(`/api/approval-workflow/start/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error('Error starting approval workflow:', error);
    return { status: 0, message: error.message };
  }
};
